import React, { useState } from "react";
import { toast } from "sonner";
import { Scissors, Loader2, X, Wand2, Merge } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

export const TrackSplitter = ({ track, onClose, onDone }) => {
  const [noise, setNoise] = useState(-30);
  const [minSilence, setMinSilence] = useState(2);
  const [minTrack, setMinTrack] = useState(45);
  const [segs, setSegs] = useState(null);
  const [busy, setBusy] = useState("");
  const [delOriginal, setDelOriginal] = useState(true);
  const [identify, setIdentify] = useState(true);
  const [identProg, setIdentProg] = useState("");

  const detect = async () => {
    setBusy("detect");
    try {
      const r = await api.post(`/rip/tracks/${track.track_id}/split-preview`, {
        noise_db: noise, min_silence: minSilence, min_track: minTrack });
      const found = r.data.segments || [];
      setSegs(found.map((s, i) => ({ ...s, title: `${track.title || "Track"} — Part ${i + 1}` })));
      if (found.length < 2) toast.warning("Only one segment detected — try a more sensitive silence setting");
      else toast.success(`${found.length} tracks detected from ${r.data.silences_found} silence gaps`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detection failed");
    }
    setBusy("");
  };

  const mergeRow = (i) => {
    setSegs((p) => {
      const n = p.map((s) => ({ ...s }));
      if (n.length < 2) return n;
      if (i === 0) { n[1].start = n[0].start; n.splice(0, 1); }
      else { n[i - 1].end = n[i].end; n.splice(i, 1); }
      return n;
    });
  };

  const split = async () => {
    setBusy("split");
    try {
      const r = await api.post(`/rip/tracks/${track.track_id}/split`, {
        segments: segs.map((s) => ({ start: s.start, end: s.end, title: s.title })),
        delete_original: delOriginal });
      const created = r.data.created || [];
      toast.success(`Split complete — ${created.length} tracks created${r.data.deleted_original ? ", original removed" : ""}`);
      if (identify && created.length) {
        setBusy("ident");
        let hits = 0;
        for (let i = 0; i < created.length; i++) {
          setIdentProg(`${i + 1}/${created.length}`);
          try {
            const ir = await api.post(`/rip/tracks/${created[i].track_id}/identify`, {});
            if (["auto_applied", "suggested", "applied"].includes(ir.data.status)) hits++;
          } catch { /* continue with remaining parts */ }
        }
        toast.success(`Identification done — ${hits}/${created.length} part${created.length === 1 ? "" : "s"} matched`);
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
      setBusy("");
      setIdentProg("");
    }
  };

  const sel = (label, value, set, options, testid) => (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">{label}</div>
      <select value={value} onChange={(e) => set(Number(e.target.value))} data-testid={testid}
        className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs focus:border-brand-amber focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={() => !busy && onClose()} data-testid="rip-splitter-modal">
      <div className="w-full max-w-2xl rounded-xl border border-line bg-bg-surface p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Scissors className="h-4 w-4 text-brand-amber" /> Concert / DVD Track Splitter
          </h3>
          <button onClick={onClose} disabled={busy === "split" || busy === "ident"} className="text-ink-muted hover:text-ink-primary" data-testid="rip-splitter-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-ink-secondary mb-4">
          "{track.title}" · {fmt(track.duration || 0)} — silence gaps become cut points; each part becomes its own track in this session.
        </p>

        <div className="grid grid-cols-3 gap-2 mb-3">
          {sel("Silence sensitivity", noise, setNoise,
            [[-25, "Strict (-25 dB)"], [-30, "Normal (-30 dB)"], [-35, "Sensitive (-35 dB)"], [-40, "Very sensitive (-40 dB)"]],
            "rip-split-noise")}
          {sel("Min silence gap", minSilence, setMinSilence,
            [[1, "1 second"], [1.5, "1.5 seconds"], [2, "2 seconds"], [3, "3 seconds"]],
            "rip-split-minsilence")}
          {sel("Min track length", minTrack, setMinTrack,
            [[30, "30 seconds"], [45, "45 seconds"], [60, "1 minute"], [90, "1.5 minutes"]],
            "rip-split-mintrack")}
        </div>

        <button onClick={detect} disabled={!!busy} data-testid="rip-split-detect-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/60 text-brand-amber px-3 py-2 text-xs font-bold hover:bg-brand-amber/10 transition disabled:opacity-50">
          {busy === "detect" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          {segs ? "Re-detect tracks" : "Detect tracks"}
        </button>

        {segs && (
          <>
            <div className="mt-4 space-y-1.5 max-h-72 overflow-y-auto" data-testid="rip-split-segments">
              {segs.map((s, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md border border-line/60 px-3 py-2">
                  <span className="text-[10px] font-mono text-brand-amber w-5 shrink-0">{i + 1}</span>
                  <span className="text-[11px] font-mono text-ink-muted w-28 shrink-0">
                    {fmt(s.start)} → {fmt(s.end)} · {fmt(s.end - s.start)}
                  </span>
                  <input value={s.title}
                    onChange={(e) => setSegs((p) => p.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                    data-testid={`rip-split-title-${i}`}
                    className="flex-1 min-w-0 rounded border border-line bg-bg-elevated px-2 py-1 text-xs focus:border-brand-amber focus:outline-none" />
                  {segs.length > 1 && (
                    <button onClick={() => mergeRow(i)} title="Merge with the previous part"
                      data-testid={`rip-split-merge-${i}`}
                      className="text-ink-muted hover:text-brand-amber transition">
                      <Merge className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 mt-3 cursor-pointer text-xs text-ink-secondary">
              <input type="checkbox" checked={delOriginal} onChange={(e) => setDelOriginal(e.target.checked)}
                className="accent-amber-500" data-testid="rip-split-delete-original" />
              Delete the original long recording after splitting
            </label>
            <label className="flex items-center gap-2 mt-2 cursor-pointer text-xs text-ink-secondary">
              <input type="checkbox" checked={identify} onChange={(e) => setIdentify(e.target.checked)}
                className="accent-amber-500" data-testid="rip-split-identify" />
              Auto-identify each part (audio fingerprint)
            </label>

            <button onClick={split} disabled={!!busy || !segs.length} data-testid="rip-split-confirm-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-amber text-black px-3 py-2 text-xs font-bold hover:opacity-90 transition disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />}
              {busy === "split" ? "Splitting…"
                : busy === "ident" ? `Identifying part ${identProg}…`
                : `Split into ${segs.length} tracks`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
